import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import api from "@/lib/api";

const POLL_INTERVAL = 2500;
const MAX_POLLS = 30; // ~75s

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nav = useNavigate();
  const [status, setStatus] = useState({ kind: "polling", msg: "Bekräftar betalning hos Stripe..." });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus({ kind: "error", msg: "Inget sessions-id i URL." });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus({ kind: "paid", msg: "Betalning genomförd!", data });
          setTimeout(() => nav("/billing/manage"), 1800);
          return;
        }
        if (data.status === "expired") {
          setStatus({ kind: "error", msg: "Betalsessionen har gått ut." });
          return;
        }
        if (attempts.current >= MAX_POLLS) {
          setStatus({ kind: "timeout", msg: "Bekräftelse tar ovanligt lång tid. Du kan stänga sidan – planen aktiveras automatiskt när betalningen registrerats." });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        setStatus({ kind: "error", msg: err?.response?.data?.detail || "Kunde inte hämta status." });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, nav]);

  const icons = {
    polling: <Loader2 className="w-12 h-12 text-blue-700 animate-spin" />,
    paid: <CheckCircle2 className="w-14 h-14 text-emerald-600" />,
    error: <XCircle className="w-14 h-14 text-red-600" />,
    timeout: <AlertCircle className="w-14 h-14 text-amber-600" />,
  };
  const colors = {
    polling: "bg-blue-50 border-blue-200",
    paid: "bg-emerald-50 border-emerald-200",
    error: "bg-red-50 border-red-200",
    timeout: "bg-amber-50 border-amber-200",
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-zinc-50">
      <div className={`max-w-md w-full bg-white border ${colors[status.kind]} rounded-sm p-10 text-center animate-fade-in-up`}>
        <div className="flex justify-center">{icons[status.kind]}</div>
        <h1 className="mt-6 text-2xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
          {status.kind === "paid" && "Tack för din betalning!"}
          {status.kind === "polling" && "Bearbetar..."}
          {status.kind === "error" && "Något gick fel"}
          {status.kind === "timeout" && "Tar lite längre tid"}
        </h1>
        <p className="mt-3 text-sm text-zinc-700 leading-relaxed">{status.msg}</p>
        {status.kind === "paid" && status.data && (
          <div className="mt-5 pt-5 border-t border-zinc-200 text-sm text-zinc-600">
            Plan: <b className="text-zinc-900 capitalize">{status.data.plan_id}</b><br/>
            Belopp: <b className="text-zinc-900">{Number(status.data.amount_total/100).toLocaleString("sv-SE")} {status.data.currency?.toUpperCase()}</b>
          </div>
        )}
        {status.kind !== "polling" && (
          <button onClick={() => nav("/billing/manage")} data-testid="back-to-billing"
            className="mt-7 w-full px-4 py-2.5 bg-blue-700 hover:bg-blue-800 text-white rounded-sm text-sm font-medium">
            Gå till mitt konto
          </button>
        )}
      </div>
    </div>
  );
}
